/**
 * AppShell — the Vera design-system application shell: a collapsible left
 * sidebar (logo, role-aware nav, operator footer) plus a slim topbar over the
 * routed content (<Outlet />). Nav items are filtered by the hierarchical role
 * (`reviewer ⊂ operator ⊂ admin`) via roleAtLeast.
 *
 * The desktop sidebar is HIDDEN by default (owner decision 2026-07-04: few
 * menu items) and toggles from the topbar hamburger; the choice persists in
 * localStorage. On narrow viewports the same button opens the temporary
 * drawer, as before.
 */

import { useMemo, useState } from "react";
import {
  Avatar,
  Box,
  Drawer,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Tooltip,
  Typography,
  useMediaQuery,
  useTheme,
} from "@mui/material";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutIcon from "@mui/icons-material/Logout";
import HomeIcon from "@mui/icons-material/HomeOutlined";
import ListAltIcon from "@mui/icons-material/ListAltOutlined";
import { Link as RouterLink, Outlet, useLocation } from "react-router-dom";
import type { ReactNode } from "react";
import type { Role } from "../api/client";
import { useAuth } from "../auth/AuthProvider";
import { roleAtLeast, roleLabel } from "../auth/roles";
import { DemoBadge } from "./DemoBadge";
import { VeraMark } from "./Logo";
import { TENANT_BRANDED, TENANT_LOGO, TENANT_NAME } from "../branding";

/**
 * Tenant logo for the shell chrome — shown INSTEAD of the Vera mark on
 * tenant-branded builds. The asset is a pre-composed horizontal lockup
 * (mark + wordmark on ONE row) so it fits the slim topbar; `height` caps it
 * to the row.
 */
function TenantLogo({ height }: { height: number }) {
  if (!TENANT_LOGO) return null;
  return (
    <Box
      component="img"
      src={TENANT_LOGO}
      alt={TENANT_NAME || "Logotipo institucional"}
      sx={{ height, maxWidth: "100%", objectFit: "contain", display: "block" }}
    />
  );
}

const DRAWER_WIDTH = 232;

/** localStorage key for the desktop-sidebar preference ("1" = visible). */
const SIDEBAR_PREF_KEY = "vera.sidebar-open";

function readSidebarPref(): boolean {
  try {
    return localStorage.getItem(SIDEBAR_PREF_KEY) === "1";
  } catch {
    return false; // hidden by default (also when storage is unavailable)
  }
}

function writeSidebarPref(open: boolean): void {
  try {
    localStorage.setItem(SIDEBAR_PREF_KEY, open ? "1" : "0");
  } catch {
    // preference simply won't persist
  }
}

type NavItem = {
  label: string;
  to: string;
  icon: ReactNode;
  minRole: Role;
};

// "Nuevo expediente" was removed from the nav (2026-07-04): the home carga
// masiva is the single ingest door. The /expedientes/nuevo route still exists
// for direct-URL access (single-applicant intake).
const NAV_ITEMS: NavItem[] = [
  { label: "Inicio", to: "/", icon: <HomeIcon />, minRole: "reviewer" },
  {
    label: "Rondas",
    to: "/rounds",
    icon: <ListAltIcon />,
    minRole: "operator",
  },
];

export function AppShell() {
  const { user, role, signOut } = useAuth();
  const location = useLocation();
  const theme = useTheme();
  const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
  const [mobileOpen, setMobileOpen] = useState(false);
  const [sidebarOpen, setSidebarOpen] = useState(readSidebarPref);

  function toggleNav() {
    if (isDesktop) {
      setSidebarOpen((v) => {
        writeSidebarPref(!v);
        return !v;
      });
    } else {
      setMobileOpen((v) => !v);
    }
  }

  const items = useMemo(
    () => NAV_ITEMS.filter((it) => roleAtLeast(role, it.minRole)),
    [role],
  );

  const sidebar = (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        height: "100%",
        bgcolor: "background.paper",
      }}
    >
      {/* Brand — the tenant lockup replaces the Vera mark on branded builds. */}
      <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 2.25, pt: 2.25, pb: 2 }}>
        {TENANT_BRANDED ? (
          <TenantLogo height={34} />
        ) : (
          <>
            <VeraMark size={30} style={{ color: "var(--brand)" }} />
            <Typography
              sx={{ fontWeight: 700, fontSize: 21, letterSpacing: "-0.02em", color: "primary.main" }}
            >
              Vera
            </Typography>
          </>
        )}
      </Box>

      {/* Nav */}
      <Box sx={{ px: 1.5, flexGrow: 1 }}>
        <Box className="vera-overline" sx={{ px: 1, pt: 1.25, pb: 0.75 }}>
          Operación
        </Box>
        <List disablePadding sx={{ display: "flex", flexDirection: "column", gap: 0.25 }}>
          {items.map((it) => {
            const selected = location.pathname === it.to;
            return (
              <ListItemButton
                key={it.label}
                component={RouterLink}
                to={it.to}
                selected={selected}
                onClick={() => setMobileOpen(false)}
                sx={{ py: 1, px: 1, color: "text.secondary" }}
              >
                <ListItemIcon sx={{ minWidth: 32, color: "inherit" }}>{it.icon}</ListItemIcon>
                <ListItemText
                  primary={it.label}
                  slotProps={{ primary: { sx: { fontSize: "0.84375rem", fontWeight: selected ? 600 : 500 } } }}
                />
              </ListItemButton>
            );
          })}
        </List>
      </Box>

      {/* Operator footer */}
      <Box sx={{ p: 1.5, borderTop: "1px solid", borderColor: "divider" }}>
        <Box sx={{ display: "flex", alignItems: "center", gap: 1.25, px: 0.75, py: 0.5 }}>
          <Avatar
            sx={{
              width: 34,
              height: 34,
              fontSize: "0.8125rem",
              fontWeight: 600,
              bgcolor: "var(--tinta-100)",
              color: "var(--tinta-700)",
              border: "1px solid var(--tinta-200)",
              flexShrink: 0,
            }}
          >
            {user?.email?.[0]?.toUpperCase() ?? "?"}
          </Avatar>
          <Box sx={{ minWidth: 0 }}>
            <Typography
              sx={{ fontSize: "0.8125rem", fontWeight: 600, color: "text.primary", lineHeight: 1.3 }}
              noWrap
            >
              {user?.email}
            </Typography>
            {role && (
              <Typography sx={{ fontSize: "0.6875rem", color: "text.secondary", lineHeight: 1.3 }}>
                {roleLabel(role)}
              </Typography>
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "background.default" }}>
      {/* Collapsible sidebar (desktop) — hidden by default, topbar toggles it */}
      <Box
        component="nav"
        sx={{
          width: { md: sidebarOpen ? DRAWER_WIDTH : 0 },
          flexShrink: { md: 0 },
          display: { xs: "none", md: sidebarOpen ? "block" : "none" },
          borderRight: "1px solid",
          borderColor: "divider",
        }}
      >
        <Box sx={{ position: "fixed", width: DRAWER_WIDTH, height: "100vh" }}>{sidebar}</Box>
      </Box>

      {/* Temporary drawer (mobile) */}
      <Drawer
        variant="temporary"
        open={mobileOpen}
        onClose={() => setMobileOpen(false)}
        ModalProps={{ keepMounted: true }}
        sx={{
          display: { xs: "block", md: "none" },
          "& .MuiDrawer-paper": { width: DRAWER_WIDTH, boxSizing: "border-box" },
        }}
      >
        {sidebar}
      </Drawer>

      {/* Main column */}
      <Box
        sx={{
          flexGrow: 1,
          minWidth: 0,
          display: "flex",
          flexDirection: "column",
          width: { md: sidebarOpen ? `calc(100% - ${DRAWER_WIDTH}px)` : "100%" },
        }}
      >
        {/* Slim topbar */}
        <Box
          component="header"
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.5,
            px: { xs: 2, md: 3.5 },
            py: 1.5,
            borderBottom: "1px solid",
            borderColor: "divider",
            bgcolor: "background.paper",
            position: "sticky",
            top: 0,
            zIndex: 10,
          }}
        >
          <Tooltip title={sidebarOpen ? "Ocultar menú" : "Mostrar menú"}>
            <IconButton
              aria-label="mostrar u ocultar el menú"
              edge="start"
              onClick={toggleNav}
            >
              <MenuIcon />
            </IconButton>
          </Tooltip>
          {/* Brand in the topbar whenever the sidebar (which carries it) is hidden.
              On tenant-branded builds the tenant lockup (logo + wordmark on one
              row) REPLACES the Vera mark, capped to the topbar height. */}
          <Box
            sx={{
              flexGrow: 1,
              display: { xs: "flex", md: sidebarOpen ? "none" : "flex" },
              alignItems: "center",
              gap: 1,
              minWidth: 0,
            }}
          >
            {TENANT_BRANDED ? (
              <TenantLogo height={26} />
            ) : (
              <VeraMark size={24} style={{ color: "var(--brand)", flexShrink: 0 }} />
            )}
          </Box>
          <Box sx={{ flexGrow: { xs: 0, md: sidebarOpen ? 1 : 0 } }} />
          <DemoBadge />
          <Tooltip title="Cerrar sesión">
            <IconButton
              aria-label="cerrar sesión"
              onClick={() => void signOut()}
              sx={{
                border: "1px solid",
                borderColor: "divider",
                color: "text.secondary",
                width: 34,
                height: 34,
              }}
            >
              <LogoutIcon sx={{ fontSize: 18 }} />
            </IconButton>
          </Tooltip>
        </Box>

        {/* Routed content */}
        <Box component="main" sx={{ flexGrow: 1 }}>
          <Box sx={{ p: { xs: 2, md: 3 }, maxWidth: 1280, mx: "auto" }}>
            <Outlet />
          </Box>
        </Box>

        {/* Footer — on tenant-branded consoles this is the ONLY place the
            Vera mark appears (the tenant owns the rest of the chrome). */}
        {TENANT_BRANDED && (
          <Box
            component="footer"
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "center",
              gap: 0.75,
              px: { xs: 2, md: 3.5 },
              py: 1.5,
              borderTop: "1px solid",
              borderColor: "divider",
              color: "text.secondary",
            }}
          >
            <VeraMark size={16} style={{ color: "currentColor", opacity: 0.7 }} />
            <Typography sx={{ fontSize: "0.75rem" }}>
              Impulsado por Vera — plataforma de validación documental
            </Typography>
          </Box>
        )}
      </Box>
    </Box>
  );
}
